export default function createState() {
  return {
    folders: [],
    tasks: [],
    // Flag tornado true após o primeiro `SET_TASKS` (incluindo resposta
    // vazia). Permite que `task-list-view` distinga cold-load REAL (mostra
    // skeleton) de reloads subsequentes (mostra apenas o progress-linear
    // no topo, mantendo o conteúdo atual no lugar).
    tasksFetchedAtLeastOnce: false,
    // Erro da última carga da lista (AC-1.7). `null` = sem erro;
    // `{ message: String }` = a carga falhou. Existe para que a view distinga
    // "não foi possível carregar" de "nenhum resultado" — hoje uma falha de
    // rede produz exatamente a mesma tela que uma lista legitimamente vazia.
    // Zerado por `SET_TASKS` (um retry bem-sucedido limpa o estado anterior).
    tasksLoadError: null,
    // Sinal de perda de acesso (W8/F6). Setado por SIGNAL_ACCESS_LOST quando o
    // recurso selecionado (lista) responde 404. A view observa a mutation via
    // `store.subscribe`; este campo existe para observabilidade (devtools) e
    // possível watch futuro. null = nenhum sinal pendente.
    accessLost: null,
    tags: [],
    activeFolderId: null,
    activeSprintId: null,
    // Modo "visão rápida": quando setado ('mine'|'today'|'next7'|'overdue'|
    // 'noDue'), o board lista tarefas cross-pasta filtradas por critério e
    // pasta/sprint ficam deselecionadas (mutuamente exclusivo com
    // activeSprintId). `quickViewCounts` mapeia os contadores reais vindos do
    // backend; chave ausente / valor <= 0 → badge oculto no componente.
    activeQuickView: null,
    quickViewCounts: {},
    // Paginação da visão rápida (spec-002 F4). `total` é o número de LINHAS-RAIZ
    // do conjunto inteiro, não das carregadas — é ele que diz se ainda há página
    // por buscar. Sem paginação pedida, o backend responde array e a camada de
    // API preenche `total` com o tamanho da lista, então o consumidor não precisa
    // saber qual forma do contrato veio.
    quickViewPagination: { page: 1, pageSize: 0, total: 0 },
    loading: false,
    dateFilter: { startDate: null, endDate: null },
    // Closed-task visibility (session-only, NOT persisted). AC-9.
    // includeClosed → Board toggle "Mostrar fechadas" (list/kanban only).
    includeClosed: false,
    // Client-side search query applied over `currentSprintTasks` (matches no
    // título da tarefa, accent + case insensitive via `normalizeText`).
    // Session-only, NOT persisted em URL — busca livre não tem valor de
    // bookmark e simplifica o pipeline. String vazia = sem filtro.
    searchQuery: '',
    // Client-side status filter (customizar-status-tarefas, AC-13). Array of
    // statusIds; applied over `currentSprintTasks` like `searchQuery` (no
    // backend hit). Empty = no filter. Driven by the active folder's statuses
    // slice. Kept client-side because the board already has the tasks in the
    // Vuex cache and the status set is folder-scoped — filtering is instant.
    statusFilter: [],
    overviewStats: {
      total: 0,
      todo: 0,
      doing: 0,
      overdue: 0,
    },
    assignees: {
      professionals: [],
      clients: [],
    },
    assigneesLastUpdate: null,
    assigneesLoading: false,
    tagsLastUpdate: null,
    // Per-folder custom statuses (feature: customizar-status-tarefas).
    // Keyed by folderId because the board can switch folders and each owns
    // its own ordered status set. NEVER store a flat `statuses` array — the
    // board resolves the active sprint's folder first, then reads its slice.
    //   statusesByFolder: { [folderId]: [...statusObjects ordered by position] }
    //   statusesLastUpdate: { [folderId]: <ms timestamp> } (TTL cache guard)
    //   statusesLoading: { [folderId]: <bool> } (per-folder in-flight flag)
    statusesByFolder: {},
    statusesLastUpdate: {},
    statusesLoading: {},
    customFields: [],
    taskDetail: null,
    // Preferência PESSOAL de colunas da List view, escopada por sprint
    // (pm_user_list_preference no backend). `columns: null` = sem preferência
    // salva → o getter `visibleListColumns` aplica o default do registry.
    // `sprintId` marca a qual sprint a preferência carregada pertence — o
    // getter SÓ aplica `columns` quando `sprintId === activeSprintId` (race
    // guard contra troca rápida de sprint com fetch em voo).
    listPreference: { sprintId: null, columns: null, loading: false },
    // Ordenamento multi-coluna controlado pelo usuário (cabeçalhos clicáveis
    // da List + toolbar Sort control). Array ORDENADO de critérios; o índice 0
    // é a coluna primária (badge "1"), o índice 1 a secundária, etc. Chain
    // vazio === "Manual (arrastar)": cai no comportamento atual de sortOrder
    // (null-last). Cada entry é `{ field, direction }` com `direction` 'asc'/
    // 'desc'. O getter `tasksByStatus` aplica o comparador tipado em cascata
    // (field1; empate → field2; …; empate final → sortOrder). O Kanban NUNCA
    // consome o chain (sempre manual, via `kanbanColumnsManual`).
    sortChain: [],
    // Server-side filters applied to the Board (kanban/list views).
    // Todos os 3 são arrays (multi-select OR-within-filter no backend
    // via `= ANY(...)`). O legacy `dateFilter` é mantido separado porque
    // pertence ao contexto da sprint ativa e é resetado na troca de sprint.
    boardFilters: {
      responsavelIds: [],
      clientIds: [],
      tagIds: [],
    },
    // Server-side filters applied to the Overview tab (5 panels).
    // `startDate` / `endDate` use DD/MM/YYYY strings (matching the FE
    // `date_new_ux` input contract) — the API layer converts them when
    // composing the query string.
    overviewFilters: {
      startDate: null,
      endDate: null,
      responsavelIds: [],
      clientIds: [],
    },
    overview: {
      overdueTasks: [],
      weeklyDue: [],
      weeklyDone: [],
      memberDistribution: { members: [], hasOverdue: false },
      clientDistribution: { members: [], hasOverdue: false },
      chartMonthly: { categories: [], created: [], resolved: [] },
      loading: {
        overdueTasks: false,
        weeklyDue: false,
        weeklyDone: false,
        memberDistribution: false,
        clientDistribution: false,
        chartMonthly: false,
      },
      lastUpdate: {
        overdueTasks: null,
        weeklyDue: null,
        weeklyDone: null,
        memberDistribution: null,
        clientDistribution: null,
        chartMonthly: null,
      },
    },
    // Duplicate View / Espelhamento de tarefa
    taskMirrors: {},
    taskMirrorsLoading: {},
    // Agenda (calendário semana/mês) — slice ISOLADO do resto do board.
    // Decisão de produto (2026-08-28): a Agenda busca a PRÓPRIA janela de
    // datas (mês/semana visível no calendário) e NUNCA usa `dateFilter` — se
    // ela reaproveitasse `dateFilter`/`tasks`, navegar de mês na Agenda
    // mudaria silenciosamente o que Lista e Kanban mostram (e a URL de
    // filtros do board). Por isso o par tasks/window/loading/error é
    // inteiramente separado dos equivalentes do board.
    agendaTasks: [],
    // `{ startDate, endDate, key }` (YYYY-MM-DD) da janela atualmente
    // exibida — produzido por `utils/calendarWindow.js#resolveCalendarWindow`.
    // `key` é o token de race-guard: a action `loadAgendaTasks` só aplica a
    // resposta se `agendaWindow.key` ainda for o mesmo no momento em que ela
    // chega (descarta respostas obsoletas de cliques rápidos em `>`/`<`).
    agendaWindow: null,
    agendaLoading: false,
    // Erro da última carga da Agenda. Ao contrário de `tasksLoadError`, uma
    // falha aqui NÃO zera `agendaTasks` — a view mantém a última janela
    // válida renderizada em vez de esvaziar o calendário numa falha
    // transiente (ex.: perda momentânea de rede ao navegar de mês).
    agendaError: null,
  }
}
